/** Product reviews moderation: status tabs, approve / reject / spam, reply, delete, bulk actions. */
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Empty, Input, Popconfirm, Rate, Result, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { fmtDate, listOf, sitePath, toastErr, totalOf, useSite } from './common';
import { Link } from 'react-router-dom';

const { Text, Paragraph } = Typography;
const ST = { pending: 'gold', approved: 'green', rejected: 'default', spam: 'red' };

const Reply = ({ r, onSave }) => {
    const { t } = useTranslation();
    const [v, setV] = useState(r.reply || '');
    const [open, setOpen] = useState(!!r.reply);
    if (!open) return <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setOpen(true)}>{t('Reply')}</Button>;
    return (
        <div style={{ marginTop: 6 }}>
            <Input.TextArea size="small" autoSize={{ minRows: 2 }} value={v} onChange={(e) => setV(e.target.value)} placeholder={t('Public reply')} />
            {v !== (r.reply || '') && <Button size="small" type="primary" style={{ marginTop: 4 }} onClick={() => onSave(v)}>{t('Save reply')}</Button>}
        </div>
    );
};

const ShopReviews = () => {
    const { t } = useTranslation();
    const { api, siteId } = useSite();
    const [f, setF] = useState({ status: 'pending', q: '', page: 1 });
    const [data, setData] = useState(null);
    const [soon, setSoon] = useState(false);
    const [sel, setSel] = useState([]);

    const load = useCallback(async () => {
        try {
            const d = await api.get('/reviews', { status: f.status || undefined, q: f.q || undefined, page: f.page, per_page: 25 }, QUIET);
            setData({ items: listOf(d), total: totalOf(d), pending: d?.pending || 0 });
        } catch (e) { if (e?.status === 404) setSoon(true); else toastErr(e); setData({ items: [], total: 0 }); }
    }, [api, f]);
    useEffect(() => { load(); setSel([]); }, [load]);

    const update = async (r, body) => { try { await api.put(`/reviews/${r.id}`, body); load(); } catch (e) { toastErr(e); } };
    const del = async (r) => { try { await api.del(`/reviews/${r.id}`); load(); } catch (e) { toastErr(e); } };
    const bulk = async (body) => {
        try { const res = await api.post('/reviews/bulk', { ids: sel, ...body }); message.success(t('{{n}} updated', { n: res?.updated ?? sel.length })); load(); } catch (e) { toastErr(e); }
    };

    if (soon) return <Card><Result status="info" title={t('Reviews are coming soon')} /></Card>;
    return (
        <Card size="small" title={t('Reviews')}>
            <Tabs activeKey={f.status} onChange={(status) => setF({ ...f, status, page: 1 })} items={[
                { key: 'pending', label: <Badge count={data?.pending || 0} size="small" offset={[8, 0]}>{t('Pending')}</Badge> },
                { key: 'approved', label: t('Published') }, { key: 'rejected', label: t('Rejected') }, { key: 'spam', label: t('Spam') }, { key: '', label: t('All') },
            ]} />
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder={t('Search')} style={{ width: 260 }} onSearch={(q) => setF({ ...f, q, page: 1 })} />
                {sel.length > 0 && (
                    <>
                        <Button size="small" icon={<CheckOutlined />} onClick={() => bulk({ status: 'approved' })}>{t('Publish')}</Button>
                        <Button size="small" icon={<CloseOutlined />} onClick={() => bulk({ status: 'rejected' })}>{t('Reject')}</Button>
                        <Button size="small" onClick={() => bulk({ status: 'spam' })}>{t('Spam')}</Button>
                        <Popconfirm title={t('Delete {{n}} reviews?', { n: sel.length })} onConfirm={() => bulk({ delete: true })}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                    </>
                )}
            </Space>
            <Table rowKey="id" size="small" loading={!data} dataSource={data?.items} scroll={{ x: 760 }}
                rowSelection={{ selectedRowKeys: sel, onChange: setSel }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No reviews')} /> }}
                pagination={{ current: f.page, pageSize: 25, total: data?.total, hideOnSinglePage: true, onChange: (page) => setF({ ...f, page }) }}
                columns={[
                    {
                        title: t('Review'), key: 'r', render: (_, r) => (
                            <div>
                                <Space size={6} wrap><Rate disabled value={r.rating} style={{ fontSize: 12 }} /><Text strong>{r.title}</Text>{r.verified && <Tag color="blue">{t('Verified buyer')}</Tag>}</Space>
                                <Paragraph style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }} ellipsis={{ rows: 3, expandable: true }}>{r.body}</Paragraph>
                                <Text type="secondary" style={{ fontSize: 12 }}>{r.author}{r.email ? ` · ${r.email}` : ''} · {fmtDate(r.created_at)}</Text>
                                <Reply r={r} onSave={(reply) => update(r, { reply })} />
                            </div>
                        ),
                    },
                    { title: t('Product', { context: 'shop' }), key: 'p', width: 180, render: (_, r) => <Link to={sitePath(siteId, `products/${r.product_id}`)}>{r.product_title || `#${r.product_id}`}</Link> },
                    { title: t('Status'), dataIndex: 'status', width: 100, render: (v) => <Tag color={ST[v]}>{t({ pending: 'Pending', approved: 'Published', rejected: 'Rejected', spam: 'Spam' }[v] || v)}</Tag> },
                    {
                        title: '', key: 'a', width: 120, render: (_, r) => (
                            <Space size={0}>
                                {r.status !== 'approved' && <Button size="small" type="text" icon={<CheckOutlined style={{ color: '#52c41a' }} />} title={t('Publish')} onClick={() => update(r, { status: 'approved' })} />}
                                {r.status !== 'rejected' && <Button size="small" type="text" icon={<CloseOutlined />} title={t('Reject')} onClick={() => update(r, { status: 'rejected' })} />}
                                <Popconfirm title={t('Delete?')} onConfirm={() => del(r)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
                            </Space>
                        ),
                    },
                ]} />
        </Card>
    );
};

export default ShopReviews;
